'use client';

import { Image } from '@/components/ui/image/image';
import { cn } from '@/lib/utils';
import * as Tabs from '@radix-ui/react-tabs';
import { useEffect, useRef, useState } from 'react';
import type { SlideItem } from '../../hero-slider';
import styles from './slider-tabs.module.scss';

export type SliderTabsProps = {
  slides: SlideItem[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  className?: string;
};

export const SliderTabs = ({ slides, activeIndex, onTabChange, className }: SliderTabsProps) => {
  const activeSlide = slides[activeIndex];
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Check scroll position to show/hide fade effects
  const checkScroll = () => {
    const element = listRef.current;
    if (!element) return;

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const isScrollable = scrollWidth > clientWidth;

    setShowLeftFade(isScrollable && scrollLeft > 0);
    setShowRightFade(isScrollable && scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Scroll active tab into view when activeIndex changes
  useEffect(() => {
    const activeTrigger = triggerRefs.current[activeIndex];
    if (activeTrigger) {
      activeTrigger.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeIndex]);

  // Check scroll on mount and window resize
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  return (
    <Tabs.Root
      value={activeSlide?.id}
      onValueChange={(value) => {
        const index = slides.findIndex((s) => s.id === value);
        if (index !== -1) onTabChange(index);
      }}
      className={cn(styles.root, className)}
    >
      <div className={styles.wrapper}>
        <Tabs.List ref={listRef} className={styles.list} onScroll={checkScroll}>
          {slides.map((slide, index) => (
            <Tabs.Trigger
              key={`${slide.id}-${index}`}
              value={slide.id}
              ref={(el) => {
                triggerRefs.current[index] = el;
              }}
              className={styles.trigger}
            >
              {slide.tabIcon?.src && (
                <Image
                  className={styles.trigger_icon}
                  src={slide.tabIcon.src}
                  alt={slide.tabIcon.alt || ''}
                  width={24}
                  height={24}
                />
              )}
              <span className={styles.trigger_label}>{slide.tabLabel}</span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {showLeftFade && <div className={cn(styles.fade, styles.fade_left)} />}
        {showRightFade && <div className={cn(styles.fade, styles.fade_right)} />}
      </div>
    </Tabs.Root>
  );
};

export default SliderTabs;
